import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type BlockFilter,
  type Core_BlocksQuery,
  type Core_EnableBlockRotationInput,
  type EnrollmentIre_AutoAssignBlockMembershipInput,
  type EnrollmentIre_BlockEnrollmentFilter,
  type EnrollmentIre_UpsertBlockMembership,
  type Enrollment_Ire_BlockMembershipsQuery,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { naturalSort, toast, usePreferredNameLayout } from '@tyro/core';
import { groupsKeys } from '@tyro/groups';
import { useTranslation } from '@tyro/i18n';
import { peopleKeys } from '@tyro/people';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';
import { classListManagerKeys } from './keys';

const blocks = graphql(/* GraphQL */ `
  query core_blocks($filter: BlockFilter) {
    core_blocks(filter: $filter) {
      blockId
      name
      description
      subjectGroupNamesJoined
      subjectGroupIds
      isRotation
      rotations {
        iteration
        startDate
        endDate
      }
    }
  }
`);

const blockMemberships = graphql(/* GraphQL */ `
  query enrollment_ire_blockMemberships(
    $filter: EnrollmentIre_BlockEnrollmentFilter!
  ) {
    enrollment_ire_blockMemberships(filter: $filter) {
      blockId
      block {
        blockId
        name
        description
        classGroupIds
        subjectGroupIds
      }
      isRotation
      groups {
        rotationIteration
        unenrolledStudents {
          isDuplicate
          classGroupName
          gender
          person {
            partyId
            title {
              id
              name
              nameTextId
            }
            firstName
            lastName
            avatarUrl
            type
          }
        }
        subjectGroups {
          partyId
          name
          students {
            isDuplicate
            classGroupName
            gender
            person {
              partyId
              title {
                id
                name
                nameTextId
              }
              firstName
              lastName
              avatarUrl
              type
            }
          }
          staff {
            partyId
            title {
              id
              name
              nameTextId
            }
            firstName
            lastName
            avatarUrl
            type
          }
        }
      }
    }
  }
`);

const upsertBlockMemberships = graphql(/* GraphQL */ `
  mutation enrollment_ire_upsertBlockMemberships(
    $input: EnrollmentIre_UpsertBlockMembership!
  ) {
    enrollment_ire_upsertBlockMemberships(input: $input) {
      blockId
    }
  }
`);

const enableBlockRotations = graphql(/* GraphQL */ `
  mutation core_enableBlockRotations($input: Core_EnableBlockRotationInput!) {
    core_enableBlockRotations(input: $input) {
      success
    }
  }
`);

const autoAssignBlock = graphql(/* GraphQL */ `
  mutation enrollment_ire_autoAssignBlocks(
    $input: EnrollmentIre_AutoAssignBlockMembershipInput!
  ) {
    enrollment_ire_autoAssignBlocks(input: $input) {
      success
    }
  }
`);
const blocksQuery = (filter: BlockFilter) => ({
  queryKey: classListManagerKeys.blocksList(filter),
  queryFn: async () => {
    const { core_blocks: coreBlocks } = await gqlClient.request(blocks, {
      filter,
    });
    return {
      core_blocks: coreBlocks.sort((prev, next) =>
        prev.blockId.localeCompare(next.blockId),
      ),
    };
  },
});

export function useBlocksList(yearGroupId: number) {
  return useQuery({
    ...blocksQuery({ yearGroupIds: [yearGroupId] }),
    enabled: !!yearGroupId,
    select: useCallback(
      ({ core_blocks }: Core_BlocksQuery) =>
        core_blocks.sort((prev, next) => naturalSort(prev.name, next.name)),
      [],
    ),
  });
}

export function getBlocksList(yearGroupId: number) {
  return queryClient.fetchQuery(blocksQuery({ yearGroupIds: [yearGroupId] }));
}

const blockMembershipsQuery = (
  filter: EnrollmentIre_BlockEnrollmentFilter,
) => ({
  queryKey: classListManagerKeys.blockMemberships(filter),
  queryFn: () => gqlClient.request(blockMemberships, { filter }),
});

export function useBlockMemberships(blockId: string | null) {
  const { sortByDisplayName } = usePreferredNameLayout();
  return useQuery({
    ...blockMembershipsQuery({ blockId: blockId || '' }),
    enabled: !!blockId,
    select: useCallback(
      ({
        enrollment_ire_blockMemberships,
      }: Enrollment_Ire_BlockMembershipsQuery) => ({
        ...enrollment_ire_blockMemberships,
        id: nanoid(4),
        groups: enrollment_ire_blockMemberships.groups.map((group) => ({
          ...group,
          unenrolledStudents: group.unenrolledStudents
            .sort((a, b) => sortByDisplayName(a.person, b.person))
            .map((student) => ({
              ...student,
              id: String(student.person.partyId),
            })),
          subjectGroups: group.subjectGroups.map((subjectGroup) => ({
            ...subjectGroup,
            students: subjectGroup.students
              .sort((a, b) => sortByDisplayName(a.person, b.person))
              .map((student) => ({
                ...student,
                id: student.isDuplicate
                  ? `${student.person.partyId}-${nanoid(10)}`
                  : String(student.person.partyId),
              })),
          })),
        })),
      }),
      [sortByDisplayName],
    ),
  });
}

export function useUpdateBlockMemberships() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: EnrollmentIre_UpsertBlockMembership) =>
      gqlClient.request(upsertBlockMemberships, { input }),
    onSuccess: async () => {
      toast.success(t('common:snackbarMessages.updateSuccess'));
      await queryClient.invalidateQueries({
        queryKey: classListManagerKeys.allBlockMemberships(),
      });
      queryClient.invalidateQueries({ queryKey: groupsKeys.all });
      queryClient.invalidateQueries({ queryKey: peopleKeys.all });
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useAutoAssignBlock() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: EnrollmentIre_AutoAssignBlockMembershipInput) =>
      gqlClient.request(autoAssignBlock, { input }),
    onSuccess: async () => {
      toast.success(t('common:snackbarMessages.updateSuccess'));
      await queryClient.invalidateQueries({
        queryKey: classListManagerKeys.all,
      });
      queryClient.invalidateQueries({ queryKey: groupsKeys.all });
      queryClient.invalidateQueries({ queryKey: peopleKeys.all });
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useCreateOrUpdateBlockRotation(isEdit: boolean) {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationKey: classListManagerKeys.createOrUpdateBlockRotation(),
    mutationFn: async (input: Core_EnableBlockRotationInput) =>
      gqlClient.request(enableBlockRotations, { input }),
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSuccess: () => {
      toast.success(
        isEdit
          ? t('common:snackbarMessages.updateSuccess')
          : t('common:snackbarMessages.createSuccess'),
      );
      queryClient.invalidateQueries({ queryKey: classListManagerKeys.all });
    },
  });
}

export type ReturnTypeOfUseBlockList = UseQueryReturnType<typeof useBlocksList>;

export type ReturnTypeOfUseBlockMemberships = UseQueryReturnType<
  typeof useBlockMemberships
>;
